import {Component, OnInit, ViewChild} from '@angular/core';
import {
    FormArray,
    FormControl, FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import {BaseView} from '../../../../../../core/view/base.view';
import {PartnerType} from '../../../../../../partner/common/api/model/partnerSummary.model';
import {BehaviorSubject} from 'rxjs';
import {SelectFilterElement} from '../../../../../../../components/common/select-filter/select-filter.model';
import {MomentAdapter, MomentUtils} from '../../../../../../../shared/utils/moment.utils';
import {ObjectUtils} from '../../../../../../../shared/utils/object.utils';
import {CustomDateModel} from '../../../../../../common/custom-date/entity/custom-date.model';
import {MedicalPainSaveOneService} from '../../../impl/service/medicalPainSaveOne.service';
import {MedicalRecordPainCreateUpdatePayload} from '../../../impl/service/payload/save/medicalRecordPainCreateUpdate.payload';
import {CustomDateInputComponent} from '../../../../../../../components/common/date/custom-date/custom-date-input/custom-date-input.component';
import {MedicalRecordPainModel} from '../../model/medicalRecordPain.model';
import {ConsultationSummaryModel} from '../../../../../consultation/consultation/api/model/consultationSummary.model';
import {IDialogComponent} from '../../../../../../common/dialog/iDialog.component';
import {DialogWithInput} from '../../../../../../common/dialog/dialogWithInput';
import {
    CreateUpdateDefaultComponent
} from '../../../../../../core/component/create-update-default/create-update-default.component';
import {MedicalPainFindOneByIdService} from '../../../impl/service/medicalPainFindOneById.service';
import {Moment} from 'moment';
import {
    TranscriptionConfig
} from "../../../../../../common/audio/transcription/api/component/audio-transcription/audio-transcription.component";
import {MedicalRecordPainIntensityModel} from "../../model/medicalRecordPainIntensity.model";
import {
    MedicalRecordPainDeleteIntensityActionService
} from "../../../impl/service/medicalRecordPainDeleteIntensityAction.service";
import {
    ConsultationTranscriptionHandler
} from "../../../../../consultation/consultation/api/service/transcription/consultationTranscriptionHandler";
import {
    AudioTranscriptionConfig
} from "../../../../../../common/audio/transcription/api/component/audio-transcription-v2/audio-transcription-v2.component";

export interface MedicalRecordPainSaveDialogInput {
    id?: number;
    partnerId?: number;
    consultationSummary?: ConsultationSummaryModel;
    startTranscription: boolean;
}
export interface MedicalRecordPainSaveDialogResult {
    entity: MedicalRecordPainModel;
    created: boolean;
}


@Component({
    selector: 'app-medical-record-pain-create-update',
    templateUrl: './medical-record-pain-create-update.component.html',
    viewProviders: [
        MedicalPainSaveOneService,
        MedicalPainFindOneByIdService,
        MedicalRecordPainDeleteIntensityActionService,
        ConsultationTranscriptionHandler
    ]
})
export class MedicalRecordPainCreateUpdateComponent extends BaseView
    implements OnInit, IDialogComponent<MedicalRecordPainSaveDialogInput, MedicalRecordPainSaveDialogResult> {
    dialog: DialogWithInput<MedicalRecordPainSaveDialogInput, MedicalRecordPainSaveDialogResult>;

    years: BehaviorSubject<SelectFilterElement<number, number>[]> = new BehaviorSubject<SelectFilterElement<number, number>[]>([]);
    formGroup: UntypedFormGroup;
    intensitiesForm: UntypedFormArray;
    PartnerType = PartnerType;
    toInitialDate: Moment;
    toInitialTypeValue: string;
    audioTranscriptionConfig: AudioTranscriptionConfig;
    pain: MedicalRecordPainModel;

    @ViewChild('from') fromComponent: CustomDateInputComponent;
    @ViewChild('to') toComponent: CustomDateInputComponent;
    @ViewChild('createUpdateDefaultComponent') createUpdateDefaultComponent: CreateUpdateDefaultComponent<any, any, any>;
    constructor(public service: MedicalPainSaveOneService,
                public findOneService: MedicalPainFindOneByIdService,
                private formBuilder: UntypedFormBuilder,
                public deleteIntensityService: MedicalRecordPainDeleteIntensityActionService,
                public transcriptionHandler: ConsultationTranscriptionHandler) {
        super();
        const values: number[] = [];
        for (let i = MomentUtils.currentYear() + 1; i > MomentUtils.currentYear() - 50; i--) {
            values.push(i);
        }
        this.years.next(values.map(value => new SelectFilterElement(value, value, value)));

        this.intensitiesForm = this.formBuilder.array([]);
        this.formGroup = this.formBuilder.group({
            id: new UntypedFormControl(undefined),
            partnerId: new UntypedFormControl('', Validators.required),
            locations: new UntypedFormControl([]),
            whens: new UntypedFormControl([]),
            hows: new UntypedFormControl([]),
            from: new UntypedFormGroup({}),
            comment: new UntypedFormControl(undefined),
            consultationId: new FormControl(undefined),
            intensities: this.intensitiesForm,
            painStillHere: new FormControl(true),
        });
    }

    ngOnInit(): void {
        if (this.dialog.input.id) {
            this.formGroup.removeControl('intensities');
        } else {
            let context;
            if (this.dialog.input.partnerId) {
                this.formGroup.patchValue({
                    partnerId: this.dialog.input.partnerId,
                });
                context = 'partnerId=' + this.dialog.input.partnerId;
            } else if (this.dialog.input.consultationSummary) {
                this.formGroup.patchValue({
                    partnerId: this.dialog.input.consultationSummary.partner.id,
                    consultationId: this.dialog.input.consultationSummary.id,
                });
                context = 'consultationId=' + this.dialog.input.consultationSummary.id;
            }
            this.transcriptionHandler.setup({
                consultationId: this.dialog.input.consultationSummary?.id,
                partnerId: this.dialog.input.consultationSummary.partner?.id
            });

            this.audioTranscriptionConfig = {
                context,
                audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_PAIN',
                startAutomatically: false,
                enableConversationOption: false,
                handler: this.transcriptionHandler,
                showPreview: true,
            };
        }
    }

    synchronizePainStillHereControl() {
        if (this.formGroup.get('painStillHere').value === true) {
            this.formGroup.removeControl('to');
        } else {
            if (!this.formGroup.get('to')) {
                this.formGroup.addControl('to', new UntypedFormGroup({}));
            }
        }
    }

    get toFormGroup(): FormGroup {
        return this.formGroup.controls.to as FormGroup;
    }

    get fromFormGroup(): FormGroup {
        return this.formGroup.controls.from as FormGroup;
    }

    mapCustomDate(customDateModel: CustomDateModel) {
        return this.formBuilder.group({
            typeValue: customDateModel.type.value,
            date: customDateModel.date,
        });
    }

    formatLabel(value: number) {
        return value;
    }

    newItemFormGroup = (): UntypedFormGroup => {
        return this.formBuilder.group({
            intensity: new UntypedFormControl(0, Validators.required),
            date: new UntypedFormControl(this.dialog.input.consultationSummary ?
                this.dialog.input.consultationSummary.date :
                MomentAdapter.current().get(),
                Validators.required),
            comment: new UntypedFormControl(''),
        });
    }

    entityLoaded(entity: MedicalRecordPainModel) {
        console.log(['pain', entity]);
        this.pain = entity;
        this.formGroup.patchValue({
            ...entity,
            from: entity.from ? this.mapCustomDate(entity.from) : null,
            locations: entity.locations.map(value => value.label),
            whens: entity.whens.map(value => value.label),
            hows: entity.hows.map(value => value.label),
            partnerId: entity.partner.id,
            painStillHere: !entity.to,
            consultationId: entity.consultation?.id
        });
        this.synchronizePainStillHereControl();
        if (entity.to) {
            this.toInitialDate = entity.to.date;
            this.toInitialTypeValue = entity.to.type.value;
        }
        this.fromComponent.entityLoaded(entity.from);

        let context: string;
        if (!entity.consultation) {
            context = 'partnerId=' + entity.partner?.id;
        } else {
            context = 'consultationId=' + entity.consultation.id;
        }
        this.transcriptionHandler.setup({
            consultationId: entity.consultation?.id,
            partnerId: entity.partner?.id
        });

        this.audioTranscriptionConfig = {
            context,
            audioContentValue: 'AUDIO_CONTENT_MEDICAL_CONSULTATION_PAIN',
            startAutomatically: false,
            enableConversationOption: false,
            handler: this.transcriptionHandler,
            showPreview: true,
        };
    }

    buildPayload = (): MedicalRecordPainCreateUpdatePayload => {
        const payload = ObjectUtils.assign(new MedicalRecordPainCreateUpdatePayload(), this.formGroup.value, ['on', 'intensities']);
        payload.from = this.fromComponent.buildPayload();
        if (this.toComponent) {
            payload.to = this.toComponent.buildPayload();
        }
        const intensityGroup = this.formGroup.get('intensities') as FormArray;
        if (intensityGroup) {
            if (intensityGroup.controls.length === 1) {
                payload.initialIntensity = intensityGroup.controls[0].value;
            }
        }
        return payload;
    }

    entitySaved(entity: MedicalRecordPainModel) {
        this.dialog.dialog.close({
            created: !!this.formGroup.get('id').value,
            entity
        });
    }

    executeSave() {
        this.createUpdateDefaultComponent.save();
    }

    deleteIntensity(intensity: MedicalRecordPainIntensityModel) {
        this.deleteIntensityService.executeAction({
            id: intensity.id
        }).then(() => {
            this.toastControl.presentConfirmation('L\'intensité a bien été supprimée');
            this.pain.intensities = this.pain.intensities.filter(i => i.id !== intensity.id);
        }).catch((e) => {
            this.toastControl.presentError(e);
        });
    }
}
